// SPDX-License-Identifier: AGPL-3.0-or-later

import * as GuildOnboardingCommands from '@app/features/guild/commands/GuildOnboardingCommands';
import Guilds from '@app/features/guild/state/Guilds';
import GuildMembers from '@app/features/member/state/GuildMembers';
import {GuildFeatures} from '@fluxer/constants/src/GuildConstants';
import type {GuildOnboardingResponse} from '@fluxer/schema/src/domains/guild/GuildOnboardingSchemas';
import {makeAutoObservable, runInAction} from 'mobx';

class GuildOnboardingStore {
	onboardings: Record<string, GuildOnboardingResponse> = {};
	loading: Record<string, boolean> = {};
	dismissedGuildOnboarding: Record<string, boolean> = {};

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	getOnboarding(guildId: string): GuildOnboardingResponse | undefined {
		return this.onboardings[guildId];
	}

	async fetchOnboarding(guildId: string): Promise<GuildOnboardingResponse | undefined> {
		if (this.loading[guildId]) return this.onboardings[guildId];
		this.loading[guildId] = true;
		try {
			const data = await GuildOnboardingCommands.getGuildOnboarding(guildId);
			runInAction(() => {
				this.onboardings[guildId] = data;
				this.loading[guildId] = false;
			});
			return data;
		} catch (error) {
			runInAction(() => {
				this.loading[guildId] = false;
			});
			return undefined;
		}
	}

	setOnboarding(guildId: string, data: GuildOnboardingResponse): void {
		this.onboardings[guildId] = data;
	}

	dismissForSession(guildId: string): void {
		this.dismissedGuildOnboarding[guildId] = true;
	}

	hasCompletedOnboarding(guildId: string, userId: string): boolean {
		const member = GuildMembers.getMember(guildId, userId);
		return member?.hasCompletedOnboarding ?? false;
	}

	isOnboardingEnabled(guildId: string): boolean {
		const guild = Guilds.getGuild(guildId);
		if (guild?.features.has(GuildFeatures.GUILD_ONBOARDING)) return true;
		return this.onboardings[guildId]?.enabled ?? false;
	}

	shouldPromptOnboarding(guildId: string, userId: string): boolean {
		if (this.dismissedGuildOnboarding[guildId]) return false;
		if (!this.isOnboardingEnabled(guildId)) return false;
		return !this.hasCompletedOnboarding(guildId, userId);
	}
}

export default new GuildOnboardingStore();
